import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ArrowCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { ROLES } from "@/lib/constants";
import { LOCKED, menuFor } from "@/lib/menuItems";
import { MENU_CHANGED } from "@/components/layout/menuConfig";

const ROLE_OPTIONS = { ...ROLES, employee: "Employee" };
const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40 transition-colors duration-100";

// Rows for one role: default items in their saved order, with saved names and show/hide.
function rowsFor(role, saved) {
  const base = menuFor(role).map((m, i) => ({ ...m, order: saved[m.to]?.order ?? (i + 1) * 10,
    name: saved[m.to]?.label || "", hidden: Boolean(saved[m.to]?.hidden) }));
  return base.sort((a, b) => a.order - b.order);
}

function MenuRow({ r, i, last, locked, onMove, onChange }) {
  return (
    <li className={`flex flex-wrap items-center gap-2 border-2 border-black px-2 py-1.5 ${r.hidden ? "bg-zinc-100" : "bg-white"}`}
        data-testid={`menu-row-${r.to}`}>
      <div className="flex flex-col">
        <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => onMove(i, -1)}
                className="p-0.5 hover:bg-zinc-200 disabled:opacity-30" data-testid={`menu-up-${r.to}`}><ArrowUp size={12} weight="bold" /></button>
        <button type="button" aria-label="Move down" disabled={last} onClick={() => onMove(i, 1)}
                className="p-0.5 hover:bg-zinc-200 disabled:opacity-30" data-testid={`menu-down-${r.to}`}><ArrowDown size={12} weight="bold" /></button>
      </div>
      <div className="w-44 text-xs">
        <div className="font-semibold">{r.label}</div>
        <div className="font-mono text-[10px] text-zinc-500">{r.to}</div>
      </div>
      <input value={r.name} maxLength={40} placeholder={`Rename (default: ${r.label})`} onChange={(e) => onChange(i, { name: e.target.value })}
             className="min-w-[12rem] flex-1 border-2 border-black px-2 py-1 text-xs" data-testid={`menu-label-${r.to}`} />
      <label className="flex items-center gap-1.5 text-[11px]" title={locked ? "Super admins always keep this item" : ""}>
        <input type="checkbox" className="accent-black" checked={!r.hidden} disabled={locked}
               onChange={(e) => onChange(i, { hidden: !e.target.checked })} data-testid={`menu-show-${r.to}`} />
        Show
      </label>
    </li>
  );
}

export default function AdminMenuSettings() {
  const [all, setAll] = useState(null);
  const [role, setRole] = useState("admin");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/menu-config").then((r) => setAll(r.data.roles || {})).catch((e) => {
      toast.error(e.response?.data?.detail || "Couldn't load the menu settings");
      setAll({});
    });
  }, []);
  useEffect(() => { if (all) setRows(rowsFor(role, all[role] || {})); }, [all, role]);
  const locked = useMemo(() => new Set(LOCKED[role] || []), [role]);
  const move = (i, d) => setRows((rs) => { const n = [...rs]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const change = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async (items) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/menu-config", { role, items });
      setAll(data.roles || {});
      window.dispatchEvent(new Event(MENU_CHANGED));
      toast.success(`Menu saved for ${ROLE_OPTIONS[role] || role}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save the menu");
    } finally {
      setBusy(false);
    }
  };
  const saveRows = () => save(Object.fromEntries(rows.map((r, i) => [r.to, {
    order: (i + 1) * 10, hidden: r.hidden, ...(r.name.trim() ? { label: r.name.trim() } : {}) }])));
  return (
    <div className="p-4 md:p-8 space-y-4" data-testid="menu-settings-page">
      <div>
        <div className="label-mono">Settings</div>
        <h1 className="font-chivo font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Menu</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-700">
          Rename, reorder or hide the left menu for each role. Hiding only takes the item out of the menu; it doesn&apos;t
          block the page. People only ever see items their role is allowed to open.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Role
          <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 block border-2 border-black bg-white px-2 py-1.5 text-sm"
                  data-testid="menu-role-select">
            {Object.entries(ROLE_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <button type="button" onClick={saveRows} disabled={busy || !all} className={`${BTN} bg-black text-white hover:bg-emerald-700`}
                data-testid="menu-save">{busy ? "Saving…" : "Save menu"}</button>
        <button type="button" onClick={() => save({})} disabled={busy || !all} className={`${BTN} bg-white hover:bg-zinc-100 inline-flex items-center gap-1`}
                data-testid="menu-reset"><ArrowCounterClockwise size={11} weight="bold" /> Back to default</button>
      </div>
      {!all ? <div className="label-mono">Loading…</div> : (
        <ul className="max-w-4xl space-y-1.5" data-testid="menu-rows">
          {rows.map((r, i) => (
            <MenuRow key={r.to} r={r} i={i} last={i === rows.length - 1} locked={locked.has(r.to)} onMove={move} onChange={change} />
          ))}
        </ul>
      )}
    </div>
  );
}
